import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import { TimePicker } from '@material-ui/pickers'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'

import moment, { Moment } from 'config/moment'
import { Dialog, snackbar } from 'components'
import CustomTextField from 'components/CustomTextField'
import { useApiCall, useCheckFormErrors } from 'hooks'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'

import { Tag, UpdateTagResponse, UpdateTagData } from '../../types'
import { generateSubscriptionForm, updateTag } from '../../api'
import { TAG_GROUPS } from '../../consts'
import { TagAssignmentOptions, TagLoadMode } from '../TagAssignmentOptions'

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  description: [
    {
      validate: (description: string) => description.length <= 200,
      message: 'No puede superar los 200 caracteres',
    },
  ],
}

interface UpdateTagDialogProps {
  handleClose: () => void
  tagToUpdate: Tag
  onDone: (newTag: Tag) => void
}

const UpdateTagDialog: React.FC<UpdateTagDialogProps> = ({ handleClose, onDone, tagToUpdate }) => {
  const [title, setTitle] = useState(tagToUpdate.title)
  const [description, setDescription] = useState(tagToUpdate.description)
  const [active, setActive] = useState(tagToUpdate.active)
  const [timeFrom, setTimeFrom] = useState<Moment | null>(
    tagToUpdate.timeFrom ? moment(tagToUpdate.timeFrom, 'HH:mm') : null,
  )
  const [timeTo, setTimeTo] = useState<Moment | null>(
    tagToUpdate.timeTo ? moment(tagToUpdate.timeTo, 'HH:mm') : null,
  )

  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [loadMode, setLoadMode] = useState<TagLoadMode>(
    tagToUpdate.subscriptionEnabled ? 'subscription' : 'csv',
  )
  const [subscriptionDomainRestricted, setSubscriptionDomainRestricted] = useState(
    tagToUpdate.subscriptionDomainRestricted ?? false,
  )
  const [subscriptionRequiredDomain, setSubscriptionRequiredDomain] = useState(
    tagToUpdate.subscriptionRequiredDomain ?? '',
  )
  const [subscriptionToken, setSubscriptionToken] = useState(tagToUpdate.subscriptionToken ?? null)

  const [updateTagApi, isLoading] = useApiCall<UpdateTagData, UpdateTagResponse>(updateTag)
  const [generateFormApi, isGeneratingForm] = useApiCall<number, UpdateTagResponse>(
    generateSubscriptionForm,
  )

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setTitle(e.target.value)
  const handleDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setDescription(e.target.value)
  const handleActiveChange: React.ChangeEventHandler<HTMLInputElement> = () => setActive(!active)
  const handleTimeFromChange = (newDate: Moment | null) => setTimeFrom(newDate)
  const handleTimeToChange = (newDate: Moment | null) => setTimeTo(newDate)
  const handleSubscriptionRequiredDomainChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setSubscriptionRequiredDomain(e.target.value)

  const isCsvTag = tagToUpdate.group === TAG_GROUPS.FROM_CSV
  const isSubscriptionMode = isCsvTag && loadMode === 'subscription'

  // eslint-disable-next-line no-shadow
  const validateCSV = (file: File): Promise<string | null> => {
    return new Promise(resolve => {
      if (!file.name.endsWith('.csv')) {
        resolve('Solo se permiten archivos .csv')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const text = reader.result as string
        const lines = text
          .split('\n')
          .map(line => line.trim())
          .filter(line => line !== '')
        if (lines.length === 0) {
          resolve('El archivo está vacío.')
          return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const invalidEmails = lines.filter(line => !emailRegex.test(line.trim()))

        if (invalidEmails.length > 0) {
          resolve('El archivo contiene correos inválidos')
          return
        }
        resolve(null)
      }
      reader.onerror = () => resolve('Error al leer el archivo')
      reader.readAsText(file)
    })
  }

  // eslint-disable-next-line no-shadow
  const handleFileChange = async (file: File) => {
    const error = await validateCSV(file)
    setFileError(error)
    if (!error) setFile(file)
    else setFile(null)
  }

  const fields = { title, description }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(fields, rules)

  const subscriptionDomainInvalid =
    isSubscriptionMode && subscriptionDomainRestricted && !subscriptionRequiredDomain.trim()

  const isSubmitDisabled =
    hasErrors || isAnyFieldEmpty || isLoading || !!fileError || subscriptionDomainInvalid

  const handleGenerateSubscriptionForm = async () => {
    try {
      const { tag } = await generateFormApi(tagToUpdate.id)
      setSubscriptionToken(tag.subscriptionToken ?? null)
      setLoadMode('subscription')
      snackbar.show('Formulario de suscripción generado')
    } catch {
      snackbar.show('No se pudo generar el formulario. Intente de nuevo.')
    }
  }

  const onUpdateTag = async () => {
    if (tagToUpdate.group === 'TIME_SLOT' && timeFrom && timeTo) {
      if (timeFrom.isSame(timeTo)) {
        snackbar.show('La hora de inicio y fin no pueden ser iguales.')
        return
      }
      // Overnight (from > to) only allowed for DINNER
      if (timeFrom.isAfter(timeTo) && tagToUpdate.code !== 'DINNER') {
        snackbar.show('La hora de inicio debe ser menor que la hora de fin.')
        return
      }
    }

    if (subscriptionDomainInvalid) {
      snackbar.show('Debés indicar el dominio requerido')
      return
    }

    const payload: UpdateTagData = {
      id: tagToUpdate.id,
      payload: {
        title,
        description,
        active,
        ...(file && !isSubscriptionMode ? { file } : {}),
        ...(tagToUpdate.group === 'TIME_SLOT'
          ? {
              timeFrom: timeFrom?.format('HH:mm'),
              timeTo: timeTo?.format('HH:mm'),
            }
          : {}),
        ...(isCsvTag
          ? {
              subscriptionEnabled: isSubscriptionMode,
              subscriptionDomainRestricted: isSubscriptionMode
                ? subscriptionDomainRestricted
                : false,
              subscriptionRequiredDomain:
                isSubscriptionMode && subscriptionDomainRestricted
                  ? subscriptionRequiredDomain.trim()
                  : null,
            }
          : {}),
      },
    }

    try {
      const { tag: updatedTag } = await updateTagApi(payload)
      onDone(updatedTag)
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/member-delimiter-style
      const error = err as { status?: number; message?: string }
      if (error?.status === 400) {
        snackbar.show(error?.message || 'Error al actualizar la etiqueta')
      } else {
        snackbar.show('No se pudo editar la etiqueta(tag). Intente de nuevo.')
      }
    }
  }

  return (
    <Dialog
      title="Editar etiqueta(tag)"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateTag}
    >
      <Grid container spacing={3}>
        <Grid item xs={11}>
          <CustomTextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            maxLength={100}
            error={errors.title.hasError}
            helperText={errors.title.message}
          />
        </Grid>
        {tagToUpdate.group !== 'TIME_SLOT' && (
          <Grid item xs={11}>
            <CustomTextField
              label="Descripción"
              value={description}
              fullWidth
              onChange={handleDescriptionChange}
              disabled={isLoading}
              required
              maxLength={200}
              error={errors.description.hasError}
              helperText={errors.description.message}
            />
          </Grid>
        )}
        {tagToUpdate.group === 'TIME_SLOT' && (
          <>
            <Grid item xs={11}>
              <TimePicker
                value={timeFrom}
                onChange={handleTimeFromChange}
                autoOk
                format="HH:mm"
                label="Hora de inicio"
                disabled={isLoading}
                TextFieldComponent={props => (
                  <TextField
                    {...props}
                    fullWidth
                    InputProps={{
                      ...props.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarTodayIcon style={{ padding: '2px' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={11}>
              <TimePicker
                value={timeTo}
                onChange={handleTimeToChange}
                autoOk
                format="HH:mm"
                label="Hora de fin"
                disabled={isLoading}
                TextFieldComponent={props => (
                  <TextField
                    {...props}
                    fullWidth
                    InputProps={{
                      ...props.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarTodayIcon style={{ padding: '2px' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </>
        )}
        <Grid item xs={11}>
          <CustomFormControlLabel
            control={<CustomCheckbox onChange={handleActiveChange} checked={active} />}
            label="Activo"
          />
        </Grid>
        {isCsvTag && (
          <TagAssignmentOptions
            loadMode={loadMode}
            onLoadModeChange={setLoadMode}
            file={file}
            fileError={fileError}
            onFileChange={handleFileChange}
            subscriptionDomainRestricted={subscriptionDomainRestricted}
            onSubscriptionDomainRestrictedChange={() =>
              setSubscriptionDomainRestricted(!subscriptionDomainRestricted)
            }
            subscriptionRequiredDomain={subscriptionRequiredDomain}
            onSubscriptionRequiredDomainChange={handleSubscriptionRequiredDomainChange}
            subscriptionToken={subscriptionToken}
            onGenerateSubscriptionForm={handleGenerateSubscriptionForm}
            isGeneratingForm={isGeneratingForm}
            isLoading={isLoading}
            csvLabel="Cargar nuevo archivo CSV (opcional)"
          />
        )}
      </Grid>
    </Dialog>
  )
}

export { UpdateTagDialog }
